import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, Car, LogEntry } from '../lib/supabase';

// --- Keys ---
export const CAR_KEYS = {
    all: ['cars'] as const,
    lists: () => [...CAR_KEYS.all, 'list'] as const,
    list: (userId: string) => [...CAR_KEYS.lists(), userId] as const,
    slug: (slug: string) => [...CAR_KEYS.all, 'slug', slug] as const,
    details: () => [...CAR_KEYS.all, 'detail'] as const,
    detail: (id: string) => [...CAR_KEYS.details(), id] as const,
};

export const LOG_ENTRY_KEYS = {
    all: ['log_entries'] as const,
    lists: () => [...LOG_ENTRY_KEYS.all, 'list'] as const,
    list: (carId: string) => [...LOG_ENTRY_KEYS.lists(), carId] as const,
};

export const GALLERY_KEYS = {
    all: ['gallery'] as const,
    car: (carId: string) => [...GALLERY_KEYS.all, carId] as const,
};

export const PART_KEYS = {
    all: ['parts'] as const,
    list: (carId: string) => [...PART_KEYS.all, carId] as const,
};

export const NOTE_KEYS = {
    all: ['notes'] as const,
    list: (carId: string) => [...NOTE_KEYS.all, carId] as const,
};

export const FAVORITE_KEYS = {
    all: ['favorites'] as const,
    list: (userId: string) => [...FAVORITE_KEYS.all, userId] as const,
};

export const LOCATION_KEYS = {
    all: ['locations'] as const,
    list: (carId: string) => [...LOCATION_KEYS.all, carId] as const,
};

export const REMINDER_KEYS = {
    all: ['reminders'] as const,
    list: (carId: string) => [...REMINDER_KEYS.all, carId] as const,
};


// --- Fetchers ---

const fetchCars = async (userId: string) => {
    const { data, error } = await supabase
        .from('cars')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Car[];
};

const fetchCarBySlug = async (slug: string) => {
    const { data, error } = await supabase
        .from('cars')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

    if (error) throw error;
    return data as Car | null;
};

const fetchLogEntries = async (carId: string) => {
    const { data, error } = await supabase
        .from('log_entries')
        .select(`
            *,
            expenses (*),
            location:locations (*),
            log_entry_parts(spare_part_id, spare_parts(name, manufacturer))
        `)
        .eq('car_id', carId)
        .eq('is_temporary', false)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((le: any) => ({
        ...le,
        location: Array.isArray(le.location) ? le.location[0] : le.location,
        imageCount: le.image_count || 0,
        partCount: le.log_entry_parts?.length || 0
    })) as LogEntry[];
};

const fetchGallery = async (carId: string) => {
    const [expenseImagesResult, sparePartImagesResult] = await Promise.all([
        supabase
            .from('log_entry_images')
            .select('*, log_entries!inner(car_id)')
            .eq('log_entries.car_id', carId)
            .order('created_at', { ascending: false }),
        supabase
            .from('spare_part_images')
            .select('*, spare_parts!inner(car_id)')
            .eq('spare_parts.car_id', carId)
            .order('created_at', { ascending: false })
    ]);

    if (expenseImagesResult.error) throw expenseImagesResult.error;
    if (sparePartImagesResult.error) throw sparePartImagesResult.error;
    const expenseImages = (expenseImagesResult.data || []).map(img => ({
        id: img.id,
        image_url: img.image_url,
        image_url_thumb: img.image_url_thumb,
        comment: img.comment,
        created_at: img.created_at,
        expense_id: img.expense_id,
        type: 'expense' as const
    }));
    const sparePartImages = (sparePartImagesResult.data || []).map(img => ({
        id: img.id,
        image_url: img.image_url,
        image_url_thumb: img.image_url_thumb,
        comment: img.comment,
        created_at: img.created_at,
        spare_part_id: img.spare_part_id,
        type: 'part' as const
    }));

    return [...expenseImages, ...sparePartImages].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
};

const fetchSpareParts = async (carId: string) => {
    const { data, error } = await supabase
        .from('spare_parts')
        .select(`
            *, 
            spare_part_images(id, image_url, image_url_thumb, log_entry_id, note_id, comment), 
            reminders(id, remind_date, is_active, repeat_yearly), 
            log_entry_parts(spare_part_id, price_per_unit, quantity, log_entries(date)),
            note_spare_parts(note_id)
        `)
        .eq('car_id', carId)
        .eq('is_temporary', false)
        .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((part: any) => {
        const entryCount = part.log_entry_parts?.length || 0;
        const noteCount = part.note_spare_parts?.length || 0;

        // Find the most recent log_entry_part that has a price_per_unit set
        const derivedPrice: number | null = (() => {
            if (!part.log_entry_parts || part.log_entry_parts.length === 0) return null;
            const withPrice = part.log_entry_parts
                .filter((lep: any) => lep.price_per_unit != null && lep.price_per_unit > 0)
                .sort((a: any, b: any) => {
                    const dateA = a.log_entries?.date || '';
                    const dateB = b.log_entries?.date || '';
                    return dateB.localeCompare(dateA); // most recent first
                });
            return withPrice.length > 0 ? withPrice[0].price_per_unit : null;
        })();

        return {
            ...part,
            entryCount,
            noteCount,
            derivedPrice,
        };
    });
};

const fetchNotes = async (carId: string) => {
    const { data: notesData, error } = await supabase
        .from('notes')
        .select('*, reminders(id, remind_date, is_active, repeat_yearly)')
        .eq('car_id', carId)
        .eq('is_temporary', false)
        .order('created_at', { ascending: false });

    if (error) throw error;

    if (notesData) {
        return await Promise.all(notesData.map(async (note) => {
            const [imagesResult, partsResult] = await Promise.all([
                supabase.from('note_images').select('id', { count: 'exact', head: true }).eq('note_id', note.id),
                supabase.from('note_spare_parts').select('spare_part_id', { count: 'exact', head: true }).eq('note_id', note.id)
            ]);
            return {
                ...note,
                imageCount: imagesResult.count || 0,
                partCount: partsResult.count || 0
            };
        }));
    }
    return [];
};

const fetchLocations = async (carId: string) => {
    // Fetch only locations that have at least one log entry for this specific car.
    // This perfectly matches the RLS and user requirement.
    // We use !inner join on log_entries to filter by car_id at the database level.
    const { data: locationsData, error } = await supabase
        .from('locations')
        .select(`
            *,
            log_entries!inner(date, id, car_id, expenses(type))
        `)
        .eq('is_temporary', false)
        .eq('log_entries.car_id', carId)
        .eq('log_entries.is_temporary', false);

    if (error) throw error;

    // The inner join might return duplicate location rows if there are multiple log entries.
    // We need to deduplicate them and group the log entries.
    const locationMap = new Map<string, any>();

    (locationsData || []).forEach((loc: any) => {
        if (!locationMap.has(loc.id)) {
            locationMap.set(loc.id, {
                ...loc,
                // We'll collect all relevant log entries for stats
                all_log_entries: []
            });
        }
        // PostgREST with !inner might return the child as an object or array depending on the query
        // but here it was a select with a join, so loc.log_entries should be available.
        // Actually, in some PostgREST versions, !inner filter returns duplicates.
        // Let's be safe.
        const existing = locationMap.get(loc.id);
        if (loc.log_entries) {
            const entries = Array.isArray(loc.log_entries) ? loc.log_entries : [loc.log_entries];
            existing.all_log_entries.push(...entries);
        }
    });

    const locations = Array.from(locationMap.values());

    // Sort and enrich locations with all expense types used by THIS car at THIS location
    const enrichedLocations = locations.map((loc: any) => {
        const sortedEntries = (loc.all_log_entries || [])
            .filter((e: any) => e.expenses && e.expenses.length > 0)
            .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

        // Collect ALL unique non-info expense types across all log entries for this location
        const allTypes = new Set<string>();
        sortedEntries.forEach((entry: any) => {
            (entry.expenses || []).forEach((ex: any) => {
                if (ex.type && ex.type !== 'info') {
                    allTypes.add(ex.type);
                }
            });
        });

        // Keep last_expense_type for icon coloring (most recent non-info type)
        const lastEntry = sortedEntries[0];
        const lastExpenseType = lastEntry?.expenses?.find((ex: any) => ex.type && ex.type !== 'info')?.type || null;
        const entryCount = (loc.all_log_entries || []).length;

        // Clean up temporary field
        delete loc.all_log_entries;

        return {
            ...loc,
            last_expense_type: lastExpenseType,
            all_expense_types: Array.from(allTypes),
            entryCount,
            entry_count: entryCount
        };
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return enrichedLocations;
};

const fetchReminders = async (carId: string) => {
    const { data, error } = await supabase
        .from('reminders')
        .select(`
            *,
            notes (id, title, content),
            spare_parts (id, name, manufacturer, part_code)
        `)
        .eq('car_id', carId)
        .is('car_task_id', null)
        .order('remind_date', { ascending: true });

    if (error) throw error;
    return data;
};


const fetchFavoriteCars = async (userId: string) => {
    const { data, error } = await supabase
        .from('favorites')
        .select('cars(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || [])
        .map((item: any) => item.cars)
        .filter((car: any) => car !== null)
        .map((car: any) => ({ ...car, is_favorite: true }));
};

const fetchSharedCars = async (userEmail: string) => {
    // Cars where the logged-in user's email is listed as a car_admin
    const { data, error } = await supabase
        .from('car_admins')
        .select('cars(*)')
        .eq('email', userEmail.toLowerCase());

    if (error) throw error;

    return (data || [])
        .map((item: any) => item.cars)
        .filter((car: any) => car !== null)
        .map((car: any) => ({ ...car, is_shared: true })) as Car[];
};

export const SHARED_KEYS = {
    all: ['shared_cars'] as const,
    list: (email: string) => [...SHARED_KEYS.all, email] as const,
};

// --- Hooks ---

export function useCars(userId?: string) {
    return useQuery({
        queryKey: CAR_KEYS.list(userId || ''),
        queryFn: () => fetchCars(userId || ''),
        enabled: !!userId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
}

export function useCarBySlug(slug?: string) {
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: CAR_KEYS.slug(slug || ''),
        queryFn: async () => {
            const data = await fetchCarBySlug(slug || '');
            // Pre-seed the cache for details if we found the car
            if (data) {
                queryClient.setQueryData(CAR_KEYS.detail(data.id), data);
            }
            return data;
        },
        enabled: !!slug,
        staleTime: 1000 * 60 * 60, // 1 hour (slugs/cars change rarely)
    });
}

export function useLogEntries(carId?: string) {
    return useQuery({
        queryKey: LOG_ENTRY_KEYS.list(carId || ''),
        queryFn: () => fetchLogEntries(carId || ''),
        enabled: !!carId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useGallery(carId?: string) {
    return useQuery({
        queryKey: GALLERY_KEYS.car(carId || ''),
        queryFn: () => fetchGallery(carId || ''),
        enabled: !!carId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useSpareParts(carId?: string) {
    return useQuery({
        queryKey: PART_KEYS.list(carId || ''),
        queryFn: () => fetchSpareParts(carId || ''),
        enabled: !!carId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useNotes(carId?: string) {
    return useQuery({
        queryKey: NOTE_KEYS.list(carId || ''),
        queryFn: () => {
            if (!carId) return Promise.resolve([]);
            return fetchNotes(carId);
        },
        enabled: !!carId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useFavoriteCars(userId?: string) {
    return useQuery({
        queryKey: FAVORITE_KEYS.list(userId || ''),
        queryFn: () => fetchFavoriteCars(userId || ''),
        enabled: !!userId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useSharedCars(userEmail?: string | null) {
    return useQuery({
        queryKey: SHARED_KEYS.list(userEmail || ''),
        queryFn: () => fetchSharedCars(userEmail || ''),
        enabled: !!userEmail,
        staleTime: 1000 * 60 * 5,
    });
}

export function useLocations(carId?: string) {
    return useQuery({
        queryKey: LOCATION_KEYS.list(carId || ''),
        queryFn: () => fetchLocations(carId || ''),
        enabled: !!carId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useReminders(carId?: string) {
    return useQuery({
        queryKey: REMINDER_KEYS.list(carId || ''),
        queryFn: () => fetchReminders(carId || ''),
        enabled: !!carId,
        staleTime: 1000 * 60 * 5,
    });
}

// --- Mutations ---

export function useAddFavorite() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ userId, carId }: { userId: string, carId: string }) => {
            const { error } = await supabase.from('favorites').insert({ user_id: userId, car_id: carId });
            if (error) throw error;
        },
        onSuccess: (_, { userId }) => {
            queryClient.invalidateQueries({ queryKey: FAVORITE_KEYS.list(userId) });
        }
    });
}

export function useRemoveFavorite() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ userId, carId }: { userId: string, carId: string }) => {
            const { error } = await supabase.from('favorites').delete().match({ user_id: userId, car_id: carId });
            if (error) throw error;
        },
        onSuccess: (_, { userId }) => {
            queryClient.invalidateQueries({ queryKey: FAVORITE_KEYS.list(userId) });
        }
    });
}

export function useDeleteAllLogEntries() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (carId: string) => {
            const { error } = await supabase.from('log_entries').delete().eq('car_id', carId);
            if (error) throw error;
            return carId;
        },
        onSuccess: (carId) => {
            queryClient.setQueryData(LOG_ENTRY_KEYS.list(carId), []);
            // Also invalidate gallery as log entries have images
            queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.car(carId) });
        }
    });
}
